import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { useSearch, type SearchOptions } from './useSearch';

export interface PaginationState {
  current: number;
  pageSize: number;
  total: number;
}

export interface DataTableOptions<T> {
  fetchData: (
    pageNum: number,
    pageSize: number,
    custom?: string
  ) => Promise<{
    errNo: number;
    data: {
      rows: T[];
      count: number;
      pn: number;
    };
  }>;
  deleteData?: (id: number) => Promise<void>;
  errorMessage?: string;
  /**
   * 搜索配置
   */
  searchOptions?: SearchOptions;
}

export interface DataTableWithSearchReturn<T> {
  data: T[];
  loading: boolean;
  pagination: PaginationState;
  fetchData: (pn?: number, rn?: number, custom?: string) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  handlePaginationChange: (page: number, pageSize?: number) => void;
  search: ReturnType<typeof useSearch>;
}

/**
 * 带搜索功能的表格数据管理Hook
 */
export function useDataTableWithSearch<T extends Record<string, unknown>>(
  options: DataTableOptions<T>
): DataTableWithSearchReturn<T> {
  const {
    fetchData: fetchDataApi,
    deleteData,
    errorMessage = '获取数据失败',
    searchOptions,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const paginationRef = useRef(pagination);
  const fetchDataApiRef = useRef(fetchDataApi);

  // 初始化搜索Hook
  const search = useSearch(searchOptions || { fields: [] });

  // 更新 ref 中的函数引用
  useEffect(() => {
    fetchDataApiRef.current = fetchDataApi;
  }, [fetchDataApi]);

  const fetchData = useCallback(
    async (pn?: number, rn?: number, custom?: string) => {
      const pageNum = pn || paginationRef.current.current;
      const pageSize = rn || paginationRef.current.pageSize;

      setLoading(true);

      try {
        const response = await fetchDataApiRef.current(
          pageNum,
          pageSize,
          custom
        );
        if (response.errNo === 0) {
          setData(response.data.rows);
          setPagination(prev => ({
            ...prev,
            total: response.data.count,
            current: response.data.pn,
          }));
        } else {
          message.error(errorMessage);
        }
      } catch (error) {
        console.error(errorMessage, error);
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [errorMessage]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      if (!deleteData) {
        message.error('删除功能未实现');
        return;
      }
      try {
        await deleteData(id);
        message.success('删除成功');
        // 重新获取数据时保持搜索条件
        const searchCondition = search.isSearching
          ? search.buildSearchCondition(search.searchValue)
          : '';
        fetchData(1, pagination.pageSize, searchCondition);
      } catch (error) {
        console.error('删除失败:', error);
        message.error('删除失败');
      }
    },
    [deleteData, fetchData, pagination.pageSize, search]
  );

  const handlePaginationChange = useCallback(
    (page: number, pageSize?: number) => {
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: pageSize || prev.pageSize,
      }));
      // 分页时保持搜索条件
      const searchCondition = search.isSearching
        ? search.buildSearchCondition(search.searchValue)
        : '';
      fetchData(page, pageSize, searchCondition);
    },
    [fetchData, search]
  );

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  // 重写搜索处理函数，集成数据获取逻辑
  const enhancedHandleSearch = useCallback(() => {
    const searchCondition = search.buildSearchCondition(search.searchValue);
    search.setIsSearching(!!searchCondition);
    if (searchCondition) {
      fetchData(1, pagination.pageSize, searchCondition);
    } else {
      fetchData(1, pagination.pageSize);
    }
  }, [search, fetchData, pagination.pageSize]);

  // 重写重置搜索函数，集成数据获取逻辑
  const enhancedHandleResetSearch = useCallback(() => {
    search.setSearchValue('');
    search.setIsSearching(false);
    fetchData(1, pagination.pageSize);
  }, [search, fetchData, pagination.pageSize]);

  // 重写搜索对象，使用增强的函数
  const enhancedSearch = {
    ...search,
    handleSearch: enhancedHandleSearch,
    handleResetSearch: enhancedHandleResetSearch,
    handleSearchKeyPress: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        enhancedHandleSearch();
      }
    },
  };

  // 初始化数据获取
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    loading,
    pagination,
    fetchData,
    handleDelete,
    handlePaginationChange,
    search: enhancedSearch,
  };
}
